import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { RootState } from '@next/core-lib/app';
import { SummaryCardComponent } from '@next/core-lib/components';
import { TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, skipWhile, take, withLatestFrom } from 'rxjs/operators';
import { Discount } from 'src/app/settings/models/settings.model';
import { getDiscount } from 'src/app/settings/store/selectors/settings.selectors';

import {
  getCustomerProducts,
  getProducts,
} from '../../../../product/store/selectors/product.selectors';
import { Product } from '../../../../shared/models/shared.model';
import { Calculation, Feeder, FeederType, GenderOptions } from '../../../models/feed-profit.model';
import { BaselineCalculatedTableComponent } from './../baseline-feeders/baseline-calculated-table/baseline-calculated-table.component';
import { BaselineTableComponent } from './../baseline-feeders/baseline-table/baseline-table.component';
import { BASELINE_FEEDERS_HOST } from './baseline-feeders.tokens';

@Component({
  selector: 'app-baseline-feeders',
  templateUrl: './baseline-feeders.component.html',
  styleUrls: ['./baseline-feeders.component.css'],
  imports: [SummaryCardComponent, BaselineTableComponent, AsyncPipe, TranslatePipe],
  providers: [
    { provide: BASELINE_FEEDERS_HOST, useExisting: forwardRef(() => BaselineFeedersComponent) },
  ],
})
export class BaselineFeedersComponent implements AfterViewInit {
  @Input() genders: string;
  @Input() isExtendedSimulation: boolean;
  @Input() calculation: Calculation;

  products$: Observable<Product[]> = this.store.select(getProducts).pipe(
    withLatestFrom(this.store.select(getCustomerProducts)),
    skipWhile(
      ([existingProducts, customerProducts]) =>
        existingProducts === null || customerProducts === null,
    ),
    take(1),
    map(([products, customerProducts]: [Product[], Product[]]) => [
      ...(products ?? []),
      ...(customerProducts ?? []),
    ]),
  );

  discounts$: Observable<Discount> = this.store.select(getDiscount).pipe(
    skipWhile((discounts) => discounts === null || discounts === undefined),
    take(1),
  );

  startFeederRows: Feeder[] = [];
  growthFeederRows: Feeder[] = [];
  endFeederRows: Feeder[] = [];
  calculatedEndFeederRows: Feeder[] = [];

  public totalTableTotalAverages = 0;
  @ViewChildren(forwardRef(() => BaselineTableComponent))
  tableComponents: QueryList<BaselineTableComponent>;
  calculatedTableComponent: BaselineCalculatedTableComponent;
  @Output()
  feedersChanged: EventEmitter<Calculation> = new EventEmitter<Calculation>();

  constructor(
    private readonly store: Store<RootState>,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngAfterViewInit() {
    this.updateSubComponents(this.calculation);
    this.tableComponents.forEach((component) => component.updateSaveData());
    this.tableComponents.forEach((component) => component.calculatePercentagesOnInit());
    this.changeDetectorRef.detectChanges();
  }

  changeGenderSelection(value: string) {
    const genders = value === 'barrow_gilt' ? GenderOptions.BARROW_GILT : GenderOptions.BOAR_GILT;
    const previousGender = this.genders;
    this.calculation = {
      ...this.calculation,
      genders,
    };

    if (previousGender !== genders) {
      this.tableComponents.forEach((tablecomponent) => {
        tablecomponent.changeGenders(value);
      });
    }
  }

  propegateNewFeederValues(calculation: Calculation) {
    this.updateCalculation(calculation);
  }

  propegateNewCalculatedFeederValues(calculation: Calculation) {
    this.updateCalculation(calculation);
  }

  updateCalculation(calculation: Calculation, emitEvent = true) {
    if (calculation) {
      this.updateSubComponents(calculation, emitEvent);
      this.feedersChanged.emit(calculation);
    }
  }

  updateSubComponents(calculation: Calculation, emitEvent = true) {
    const feeders = this.isExtendedSimulation
      ? calculation?.extendedSimulation?.feeders
      : calculation?.feeders;

    this.startFeederRows = feeders?.filter((f) => f.type === FeederType.START_FEEDER);
    this.growthFeederRows = feeders?.filter((f) => f.type === FeederType.GROWTH_FEEDER);
    this.endFeederRows = feeders?.filter((f) => f.type === FeederType.END_FEEDER);
    if (emitEvent) {
      this.calculation = calculation;
    }

    this.calculatedEndFeederRows = this.isExtendedSimulation
      ? calculation?.extendedSimulation?.calculatedFeeder
        ? [calculation?.extendedSimulation?.calculatedFeeder]
        : []
      : calculation?.calculatedFeeder
        ? [calculation?.calculatedFeeder]
        : [];

    this.tableComponents?.forEach((comp) =>
      comp.updateData(
        feeders?.filter((f) => f.type === comp.feederType),
        this.calculatedEndFeederRows,
      ),
    );

    if (this.calculatedTableComponent) {
      this.calculatedTableComponent.calculateFeedRatios(calculation);
    }
  }
}
